<script setup lang="ts">
import type { CanvasLinkNodeViewProps } from "./types";
import { NodeViewWrapper } from "@tiptap/vue-3";
import SimpleButton from "ls/components/simple-button/SimpleButton.vue";
import { SimpleTooltip } from "ls/components/simple-tooltip";
import { AxObjectType } from "ls/features/disko/bridge/selection";
import { toHex, toInlineArgbWithAlpha } from "ls/features/disko/common/utils";
import { useDiagramObject } from "ls/features/disko/queries/diagram";
import { useCurrentFileContext } from "ls/features/disko/useCurrentFileContext";
import { CircleAlertIcon, CircleArrowRightIcon, FrameIcon, Loader2Icon, Maximize2Icon } from "lucide-vue-next";
import { computed, toRef } from "vue";
import { CanvasScreenshotNodeName } from "../../canvas-screenshot/types";
import { useSelectionIntersectsNodeView } from "../../useSelectionIntersectsNodeView";
import LinkBox from "../LinkBox.vue";
import { useEditorCanvasLink } from "./useCanvasLink";

const props = defineProps<CanvasLinkNodeViewProps>();

const selectionIntersects = useSelectionIntersectsNodeView(props, { allowEmpty: false });

const selectedOrIntersects = computed(() => {
  return props.selected || selectionIntersects.value;
});

const { shortcut } = useCurrentFileContext();
const { text, href, isPending, isPresent, navigate } = useEditorCanvasLink(shortcut, toRef(() => props.node.attrs.objectId));

const diagramObject = useDiagramObject(toRef(() => props.node.attrs.objectId));
const isSection = computed(() => {
  if (!diagramObject.data.value) return;
  return diagramObject.data.value.type === AxObjectType.Section;
});
const canConvertToScreenshot = computed(() => {
  if (!props.editor.schema.nodes[CanvasScreenshotNodeName]) return false;
  return isSection.value;
});

function convertToScreenshot(event: MouseEvent) {
  if (!canConvertToScreenshot.value) return;
  event.stopPropagation();
  event.preventDefault();
  props.editor.commands.setCanvasScreenshot({ objectId: props.node.attrs.objectId });
}

const linkIcon = computed(() => {
  if (isPending.value) return Loader2Icon;
  else if (isPresent.value) return isSection.value ? FrameIcon : CircleArrowRightIcon;
  else return CircleAlertIcon;
});

const linkVariant = computed(() => {
  if (!isPresent.value) return "broken";
  return isSection.value ? "section" : "canvas";
});

const fill = computed(() => {
  if (!diagramObject.data.value?.style.Fill?.value?.fills[0]) return "";
  return toHex(diagramObject.data.value?.style.Fill.value?.fills[0].fill["fill-color"].value);
});
const borderFill = computed(() => {
  if (!diagramObject.data.value?.style.BorderFill?.value?.fills[0]) return "";
  return toInlineArgbWithAlpha(diagramObject.data.value?.style.BorderFill.value?.fills[0].fill["fill-color"].value, 0.3);
});
const textColor = computed(() => {
  if (!diagramObject.data.value?.style.BorderFill?.value?.fills[0]) return "";
  return toHex(diagramObject.data.value?.style.BorderFill.value?.fills[0].fill["fill-color"].value);
});

const sectionStyle = computed(() => {
  if (!isPresent.value || !isSection.value) return {};
  return {
    backgroundColor: fill.value,
    color: textColor.value,
    borderColor: borderFill.value,
  };
});
</script>

<template>
  <NodeViewWrapper
    as="a"
    :href="href"
    :data-type="node.type.name"
    class="after:content-none!"
    @click="navigate"
  >
    <LinkBox
      :variant="linkVariant"
      pointer
      class="whitespace-nowrap"
      :style="sectionStyle"
      :selected="selectedOrIntersects"
    >
      <component
        :is="linkIcon"
        class="relative bottom-px mr-1 inline size-4"
        :class="{ 'animate-spin': isPending }"
      />
      <span class="whitespace-normal">{{ text }}</span>

      <SimpleTooltip v-if="isPresent && canConvertToScreenshot" content="Show image">
        <SimpleButton
          class="relative top-0.5 ml-1"
          size="auto"
          variant="ghost"
          @click="convertToScreenshot"
        >
          <Maximize2Icon class="size-4" />
        </SimpleButton>
      </SimpleTooltip>
    </LinkBox>
    <span class="inline-block w-px" />
  </NodeViewWrapper>
</template>
